import { DeltaStatic } from "react-quill-new";
import styled from "@emotion/styled";
import { ArrowBackIosNew } from "@mui/icons-material";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { theme } from "@shared/lib/styles/theme";
import { formatSimpleDate } from "@shared/lib/utils/date/date";
import { openToast, TOAST_TYPE } from "@shared/lib/utils/toast";
import { BasicEditor, LoadingSpinner } from "@/components";
import { useAccessToken } from "@/hooks/auth/use-access-token";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

import {
  getInquiryControllerGetInquiriesForInstructorQueryKey,
  getInquiryControllerGetInquiriesForStudentQueryKey,
  getInquiryControllerGetInquiryByIdQueryKey,
  useInquiryControllerDeleteInquiryByUser,
  useInquiryControllerGetInquiryById,
} from "@shared/generated/api/fn/kac/inquiry/inquiry";
import { InquiryResponseDto } from "@shared/generated/api/model";
import { useRouter } from "next/router";
import { getInstructorName } from "@/utils/inquiry";

interface StudentInquiryDetailProps {
  inquiryId: number;
  onClose: () => void;
}

const QuestionTitle = ({ inquiry }: { inquiry: InquiryResponseDto }) => (
  <Typography
    variant="h6"
    component="h2"
    sx={{
      fontSize: "20px",
      fontWeight: 700,
      letterSpacing: "-0.4px",
    }}
    gutterBottom
  >
    <Typography sx={{ display: "inline" }}>{inquiry.subject}</Typography>
  </Typography>
);

const QuestionMeta = ({ inquiry }: { inquiry: InquiryResponseDto }) => (
  <InfoRow>
    <Typography
      variant="body2"
      sx={{
        color: theme.colors.text.high.black,
      }}
    >
      {inquiry.user?.name}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: theme.colors.text.disabled.black,
      }}
    >
      |
    </Typography>
    <Typography
      variant="body2"
      color="text.primary"
    >
      {formatSimpleDate(inquiry.createdAt, { withTime: true })}
    </Typography>
  </InfoRow>
);

const QuestionCourseInfo = ({
  inquiry,
  locale,
}: {
  inquiry: InquiryResponseDto;
  locale: "ko" | "en";
}) => (
  <Box
    sx={{
      fontSize: "14px",
      fontWeight: 500,
      letterSpacing: "-0.4px",
      textAlign: "left",
      textDecorationStyle: "solid",
      textUnderlinePosition: "from-font",
      textDecorationSkipInk: "none",
      color: "#00000099",
      display: "flex",
      gap: 1,
    }}
  >
    <Typography sx={{ textDecorationLine: "underline" }}>
      {inquiry.course?.title}
    </Typography>
    <Typography
      sx={{
        color: theme.colors.text.disabled.black,
      }}
    >
      |
    </Typography>
    <Typography>{getInstructorName(inquiry.course, locale)}</Typography>
  </Box>
);

const AnswerSection = ({
  answer,
  onDelete,
  onList,
  locale,
}: {
  answer: InquiryResponseDto;
  onDelete: () => void;
  onList: () => void;
  locale: "ko" | "en";
}) => {
  const { t } = useCustomTranslation();
  return (
    <AnswerContainer>
      <Typography
        variant="h3"
        sx={{
          pt: 4,
          fontSize: "18px",
          letterSpacing: "-0.4px",
        }}
      >
        {t("answer")}
      </Typography>
      <AnswerContent>
        <Box>
          <Typography
            sx={{
              color: theme.colors.text.high,
              fontWeight: 700,
            }}
          >
            {getInstructorName(answer, locale)}
          </Typography>
          <AnswerMeta>
            {formatSimpleDate(answer.createdAt, { withTime: true })}
          </AnswerMeta>
        </Box>
        <Typography
          sx={{
            fontSize: 16,
            color: theme.colors.text.high,
          }}
        >
          {(answer.content as Partial<DeltaStatic>).ops
            ?.map((el) => el.insert)
            .join("")}
        </Typography>
      </AnswerContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
          gap: 1,
        }}
      >
        <Button
          variant="text"
          onClick={onList}
          startIcon={<ArrowBackIosNew sx={{ width: 16, height: 16 }} />}
          sx={{
            color: theme.colors.text.high.black,
            border: "1px solid #D2D2D2",
            borderRadius: 2,
            padding: "8px 16px",
            fontWeight: 700,
          }}
        >
          {t("list")}
        </Button>
        <Button
          variant="text"
          onClick={onDelete}
          sx={{
            color: theme.colors.state.error.default,
            border: "1px solid",
            borderRadius: 2,
            borderColor: theme.colors.state.error.default,
            padding: "8px 16px",
            fontWeight: 700,
          }}
        >
          {t("delete")}
        </Button>
      </Box>
    </AnswerContainer>
  );
};

const NoAnswerSection = ({
  onDelete,
  onList,
}: {
  onDelete: () => void;
  onList: () => void;
}) => {
  const { t } = useCustomTranslation();
  return (
    <AnswerContainer>
      <Typography
        variant="h3"
        sx={{
          pt: 4,
          fontSize: "18px",
          letterSpacing: "-0.4px",
        }}
      >
        {t("answer")}
      </Typography>
      <Box
        sx={{
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          padding: "16px 0",
          borderTop: "1px solid #d2d2d2",
        }}
      >
        <Typography
          sx={{
            color: theme.colors.text.medium,
            fontSize: "16px",
          }}
        >
          {t("instructor_is_verifying")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 2,
          gap: 1,
        }}
      >
        <Button
          variant="text"
          onClick={onList}
          startIcon={<ArrowBackIosNew sx={{ width: 16, height: 16 }} />}
          sx={{
            color: theme.colors.text.high.black,
            border: "1px solid #D2D2D2",
            borderRadius: 2,
            padding: "8px 16px",
            fontWeight: 700,
          }}
        >
          {t("list")}
        </Button>
        <Button
          variant="text"
          onClick={onDelete}
          sx={{
            color: theme.colors.state.error.default,
            border: "1px solid",
            borderColor: theme.colors.state.error.default,
            borderRadius: 2,
            padding: "8px 16px",
            fontWeight: 700,
          }}
        >
          {t("delete")}
        </Button>
      </Box>
    </AnswerContainer>
  );
};
export const StudentInquiryDetail = ({
  inquiryId,
  onClose,
}: StudentInquiryDetailProps) => {
  const { t } = useCustomTranslation();
  const { accessToken } = useAccessToken();
  const queryClient = useQueryClient();
  const router = useRouter();
  const locale = router.locale as "ko" | "en";

  const { data: inquiry, isLoading } = useInquiryControllerGetInquiryById(
    inquiryId,
    {
      request: { accessToken },
      query: {
        enabled: !!accessToken,
        queryKey: getInquiryControllerGetInquiryByIdQueryKey(inquiryId),
      },
    },
  );

  const deleteInquiryMutation = useInquiryControllerDeleteInquiryByUser({
    request: { accessToken },
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getInquiryControllerGetInquiriesForStudentQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getInquiryControllerGetInquiriesForInstructorQueryKey(),
        });
        onClose();
      },
      onError: (error) => {
        console.error("Failed to delete inquiry:", error);
      },
    },
  });

  const handleDelete = () => {
    // 답변이 있는 경우
    if (inquiry?.inquiries?.length) {
      openToast(TOAST_TYPE.ERROR, t("cannot_delete_answered_posts"));
      return;
    } else {
      deleteInquiryMutation.mutate({
        inquiryId,
      });
    }
  };

  if (isLoading)
    return (
      <LoadingSpinner
        className="StudentInquiryDetail"
        message={t("loading_inquiry_details")}
      />
    );
  if (!inquiry) return <div>{t("inquiry_details_not_found")}</div>;

  return (
    <Container>
      <QuestionSection>
        <QuestionInfo>
          <QuestionTitle inquiry={inquiry} />
          <QuestionMeta inquiry={inquiry} />
          <QuestionCourseInfo
            inquiry={inquiry}
            locale={locale}
          />
        </QuestionInfo>
        <BasicEditor
          initialContent={inquiry.content as any}
          onEditorContent={() => {}} // 읽기 전용이므로 빈 함수
          readOnly={true}
          showToolbar={false}
          placeholder=""
          showSubmitButton={false}
        />
      </QuestionSection>
      {inquiry.inquiries?.[0] ? (
        <AnswerSection
          answer={inquiry.inquiries[0]}
          onDelete={handleDelete}
          onList={onClose}
          locale={locale}
        />
      ) : (
        <NoAnswerSection
          onDelete={handleDelete}
          onList={onClose}
        />
      )}
    </Container>
  );
};

const Container = styled(Paper)({
  boxShadow: "none",
  letterSpacing: "-0.4px",
});

const QuestionSection = styled(Paper)({
  borderRadius: 8,
  padding: "0 16px",
  boxShadow: "none",
  border: "1px solid #D2D2D2",
});

const QuestionInfo = styled(Paper)({
  padding: "16px 0",
  boxShadow: "none",
  borderRadius: 0,
  borderBottom: "1px solid #d2d2d2",
  backgroundColor: "transparent",
});

const InfoRow = styled.div({
  display: "flex",
  gap: 8,
  marginBottom: 8,
  alignItems: "center",
  fontSize: 14,
});

const Content = styled.div({
  whiteSpace: "pre-wrap",
  padding: "16px 0",
  minHeight: 208,
  fontSize: 16,
});

const AnswerContainer = styled.div({
  padding: "16px 0",
  gap: 16,
  display: "flex",
  flexDirection: "column",
});

const AnswerContent = styled(Box)({
  whiteSpace: "pre-wrap",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  border: "1px solid #D2D2D2",
  borderRadius: 8,
});

const AnswerMeta = styled(Typography)`
  color: ${({ theme }) => theme.colors.text.medium};
  font-size: 14px;
`;
